import { memo, useMemo, useState } from 'react';
import { isEmpty, map, take } from 'lodash';
import {
  Grid, Typography, Stack, Divider,
} from '@mui/material';
import { useFirestore } from '@client/hooks/utils.hooks';
import { upsertBeacons } from '@client/slices/entities/beacons.reducer';
import { BeaconStatuses } from '@sda/enums';
import { BaseCard } from '@client/components/common/BaseCard';
import { getDateRange } from '@sda/utils/date';
import { RadioIcon } from '@client/images/feather/RadioIcon';
import { SearchToggleButton } from '@client/components/common/SearchToggleButton';
import { SearchLoadMore } from '@client/components/common/SearchLoadMore';
import { getEventLocation } from '@sda/utils/string';

interface ShowCallsProps {
  readonly profileId: string;
}

export const ShowCalls = memo<ShowCallsProps>(({ profileId }) => {
  const [limit, setLimit] = useState(10);
  const [displayHiddenCalls, setDisplayHiddenCalls] = useState(false);

  const [calls, callsLoading] = useFirestore((SDK) => SDK.Beacons.listBy((query) => [
    query.isTrue('isShowCall'),
    query.isEqualTo('beaconStatus', BeaconStatuses.ACTIVE),
    query.isEqualTo('profileId', profileId || ''),
    query.isEqualTo('isPublicCall', true),
  ], { limit }), { key: `${profileId}${limit}`, upserter: upsertBeacons });

  const callArrays = useMemo(() => {
    if (isEmpty(calls)) {
      return null;
    }

    const callArray = Object.values(calls);

    const firstArray = take(callArray, 4);
    const secondArray = callArray.slice(4);

    return [firstArray, secondArray];
  }, [calls]);

  if (isEmpty(callArrays)) {
    return null;
  }

  const [openCalls, hiddenCalls] = callArrays;

  return (
    <>
      <div>
        <Divider variant="extended" />
      </div>

      <Stack spacing={3}>
        <Typography variant="subtitle2">
          Show Calls
        </Typography>

        <div>
          <Grid
            container
            spacing={3}
          >
            {map(openCalls, (call) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={call.id}
              >
                <BaseCard
                  title={getEventLocation(call)}
                  description={getDateRange(call)}
                  descriptionIcon={RadioIcon}
                  path={`/connect/${call.id}`}
                />
              </Grid>
            ))}
          </Grid>
        </div>

        {!isEmpty(hiddenCalls) && (
          <SearchToggleButton
            selected={displayHiddenCalls}
            onChange={() => setDisplayHiddenCalls(!displayHiddenCalls)}
          >
            View More
          </SearchToggleButton>
        )}

        {Boolean(displayHiddenCalls) && !isEmpty(hiddenCalls) && (
          <>
            <div>
              <Grid
                container
                spacing={3}
              >
                {map(hiddenCalls, (call) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={call.id}
                  >
                    <BaseCard
                      title={getEventLocation(call)}
                      description={getDateRange(call)}
                      descriptionIcon={RadioIcon}
                      path={`/connect/${call.id}`}
                    />
                  </Grid>
                ))}
              </Grid>
            </div>

            {!callsLoading && Object.values(calls).length >= limit && (
              <SearchLoadMore
                onClick={() => setLimit(limit + 10)}
              />
            )}
          </>
        )}
      </Stack>
    </>
  );
});
